/** Supported operators for matching against a context value */
export type ColumnVisibilityOperator = {
    $eq?: unknown;            // context value === operand
    $ne?: unknown;            // context value !== operand
    $in?: unknown[] | string; // context value is in list — list can be a $contextRef
    $nin?: unknown[] | string;// context value is NOT in list — list can be a $contextRef
};

/**
 * A condition map: each key is either a context param name or a literal value.
 *
 * Two styles:
 *   1. Context param as key:  { "widgetProduct": { "$in": [1, 2] } }
 *      → is widgetProduct in [1, 2]?
 *
 *   2. Literal value as key + $contextRef as operand: { "AudiencePro": { "$in": "$dealerProducts" } }
 *      → is "AudiencePro" in context.dealerProducts?
 *      (When the operand starts with "$", the key is treated as a literal value)
 */
export type ColumnVisibilityCondition = Record<string, ColumnVisibilityOperator>;

/** Rule for a single column */
export type ColumnVisibilityRule = {
    /** Column is hidden when this condition evaluates to true */
    hide?: ColumnVisibilityCondition;
    /** Column is visible ONLY when this condition evaluates to true (hidden otherwise) */
    show?: ColumnVisibilityCondition;
};

/**
 * Widget-level metadata key: `column-visibility`
 * Maps column field/metric string → visibility rule
 *
 * @example
 * // Show "soldAmount" only when dealer has AudiencePro:
 * { "soldAmount": { "show": { "AudiencePro": { "$in": "$dealerProducts" } } } }
 *
 * // Hide "revenue" when selected widget product ID is 1 or 2:
 * { "revenue": { "hide": { "widgetProduct": { "$in": [1, 2] } } } }
 *
 * // Hide when dealer has none of these products:
 * { "col": { "show": { "AudiencePro": { "$in": "$dealerProducts" }, "SEO": { "$in": "$dealerProducts" } } } }
 */
export type ColumnVisibilityRules = Record<string, ColumnVisibilityRule>;

/**
 * Runtime context built from the frontend state.
 *
 * Available keys:
 *   - widgetProduct:     number[]  — product IDs selected in the widget filter
 *   - widgetProductName: string[]  — product names selected in the widget filter
 *   - dealerProducts:    string[]  — dealer's contracted product names
 */
export type ColumnVisibilityContext = Record<string, unknown>;

/** Resolves a value: if it's a "$contextRef" string, returns context[ref], otherwise returns as-is */
function resolveRef(value: unknown, context: ColumnVisibilityContext): unknown {
    if (typeof value === 'string' && value.startsWith('$')) {
        return context[value.slice(1)];
    }
    return value;
}

/** Returns true if any operator value is a $contextRef string */
function hasContextRef(operator: ColumnVisibilityOperator): boolean {
    return Object.values(operator).some(v => typeof v === 'string' && v.startsWith('$'));
}

/**
 * Evaluates a single operator object against a context value.
 * Operator array operands can be a $contextRef string that resolves to an array.
 */
function evaluateOperator(contextValue: unknown, operator: ColumnVisibilityOperator, context: ColumnVisibilityContext): boolean {
    const values = Array.isArray(contextValue) ? contextValue : [contextValue];

    if ('$eq' in operator) {
        return values.some(v => v === resolveRef(operator.$eq, context));
    }
    if ('$ne' in operator) {
        return values.every(v => v !== resolveRef(operator.$ne, context));
    }
    if ('$in' in operator) {
        const list = resolveRef(operator.$in, context);
        if (!Array.isArray(list)) return false;
        return values.some(v => list.includes(v));
    }
    if ('$nin' in operator) {
        const list = resolveRef(operator.$nin, context);
        if (!Array.isArray(list)) return true;
        return values.every(v => !list.includes(v));
    }

    return false;
}

/**
 * Evaluates a condition map against the context.
 * All keys must pass (AND semantics).
 *
 * Key resolution:
 *   - If the operator contains a $contextRef, the key is treated as a literal value
 *   - Otherwise the key is a context param name and its value is looked up
 */
function evaluateCondition(
    condition: ColumnVisibilityCondition,
    context: ColumnVisibilityContext
): boolean {
    return Object.entries(condition).every(([param, operator]) => {
        const contextValue = hasContextRef(operator) ? param : context[param];
        const result = evaluateOperator(contextValue, operator, context);
        console.debug('[col-vis evaluateCondition]', { param, operator, contextValue, resolvedOperand: resolveRef(Object.values(operator)[0], context), result });
        return result;
    });
}

/**
 * Returns true if the column (identified by col.field || col.metric) should be HIDDEN.
 *
 * Logic:
 *   - No rule for this column → visible (false)
 *   - `hide` condition is true → hidden (true)
 *   - `show` condition is false → hidden (true)
 */
export function isColumnHidden(
    colKey: string,
    rules: ColumnVisibilityRules,
    context: ColumnVisibilityContext
): boolean {
    const rule = rules[colKey];
    if (!rule) return false;

    if (rule.hide && evaluateCondition(rule.hide, context)) {
        return true;
    }
    if (rule.show && !evaluateCondition(rule.show, context)) {
        return true;
    }

    return false;
}
